import React from 'react';
import styled from '@emotion/styled';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import { useTranslate } from 'hooks/useTranslate';
import getLanguageName from 'helpers/getLanguageName';

import CheckboxIcon from 'assets/icons/checkbox.svg?react';

const FiltersHeadline = styled(Typography)(() => ({
  fontSize: 16,
  fontWeight: 500,
  marginBottom: 12,
  marginTop: 16
}));

const CheckboxStyled = styled(Checkbox)(({ theme }) => ({
  padding: 0,
  marginRight: 10,
  '&.Mui-checked': {
    color: theme.palette.primary.main
  }
}));

const FormGroupStyled = styled(FormGroup)(() => ({ marginBottom: 8 }));

const FormControlLabelStyled = styled(FormControlLabel)(() => ({
  marginLeft: 0,
  '& .MuiFormControlLabel-label': {
    fontSize: 14
  }
}));

const TextFieldStyledBordered = styled(TextField)(() => ({
  border: '1px solid rgba(10, 10, 10, 1)',
  backgroundColor: '#fff'
}));

const SalaryWrapper = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  gap: 20,
  marginBottom: 20,
  '& > div': { marginBottom: 0 }
}));

const ButtonStyledWhite = styled(Button)(({ width, theme }) => ({
  borderRadius: 8,
  fontSize: 12,
  fontFamily: 'Unbounded',
  padding: '10px 31px',
  width,
  backgroundColor: theme.palette.white.main,
  color: theme.palette.grey[600],
  zIndex: 10
}));

const ClearFilterActionButton = styled(ButtonStyledWhite)(({ theme }) => ({
  border: `1px solid ${theme.palette.grey[600]}`,
  marginTop: 12,
  width: '100%'
}));

const FilterWrapper = styled('div')(({ theme }) => ({
  [theme.breakpoints.down('sm')]: { marginTop: 24 }
}));

const CheckBoxItem = React.memo(({ item, checked, onChange }) => (
  <FormGroupStyled>
    <FormControlLabelStyled
      control={<CheckboxStyled checked={checked} onChange={onChange} icon={<CheckboxIcon />} />}
      label={item}
    />
  </FormGroupStyled>
));

const EducationListFilters = ({
  filters,
  filterOptions,
  onChangeFilter,
  onChangeString,
  onClear,
  isDurationChecked,
  onChangeDuration
}) => {
  const t = useTranslate('Courses');
  return (
    <FilterWrapper>
      <FiltersHeadline>{t('Proficiency')}</FiltersHeadline>
      {(filterOptions?.proficiency || []).map((level) => (
        <CheckBoxItem
          key={level}
          item={level}
          checked={(filters.proficiency || '').split(',').includes(level)}
          onChange={() => onChangeFilter('proficiency', level)}
        />
      ))}

      <FiltersHeadline>{t('Language')}</FiltersHeadline>
      {(filterOptions?.languages || []).map((lang) => (
        <CheckBoxItem
          key={lang}
          item={getLanguageName(lang)}
          checked={(filters.language || '').split(',').includes(lang)}
          onChange={() => onChangeFilter('language', lang)}
        />
      ))}

      <FiltersHeadline>{t('Duration')}</FiltersHeadline>
      {[
        { value: [0, 1], label: t('1day') },
        { value: [1, 30], label: t('1month') },
        { value: [30, 90], label: t('1-3months') },
        { value: [90, 180], label: t('3-6months') },
        { value: [180, 365], label: t('6-12months') },
        { value: [365, 365 * 5], label: t('from1year') }
      ].map((item) => (
        <CheckBoxItem
          key={item.label}
          item={item.label}
          checked={isDurationChecked ? isDurationChecked(item.value) : false}
          onChange={() => onChangeDuration && onChangeDuration(item.value)}
        />
      ))}

      <FiltersHeadline>{t('Cost')}</FiltersHeadline>
      <SalaryWrapper>
        <TextFieldStyledBordered
          value={filters?.cost_min || ''}
          onChange={(e) => onChangeString(e, 'cost_min')}
          placeholder={t('from')}
          variant="outlined"
          fullWidth
          slotProps={{
            htmlInput: { inputMode: 'numeric' }
          }}
        />

        <TextFieldStyledBordered
          value={filters?.cost_max || ''}
          onChange={(e) => onChangeString(e, 'cost_max')}
          placeholder={t('to')}
          variant="outlined"
          fullWidth
          slotProps={{
            htmlInput: { inputMode: 'numeric' }
          }}
        />
      </SalaryWrapper>

      <ClearFilterActionButton variant="outlined" fullWidth={true} onClick={onClear} sx={{ marginTop: 2 }}>
        {t('ClearAllFilters')}
      </ClearFilterActionButton>
    </FilterWrapper>
  );
};

export default EducationListFilters;
